import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Chip,
  IconButton,
  Typography,
  Box,
  Stack,
  Tooltip,
  Button
} from "@mui/material";
import {
  ArrowRight20Px,
  Chat20Px,
  ASTableV2Cell,
  ASTableV2Row
} from "@qubit/autoparts";

import { Fragment } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "~/app/store";

import { Recipient } from "~/features/andon/Messenger";
import {
  selectAdminSummariesGrid,
  setErrorViewForWorkstation,
  setSelectedAdminSummariesWorkstationId
} from "~/features/serviceSupport/serviceSupport.slice";
import { useFcNavigate } from "~/hooks/useFcNavigate";

import {
  extractDisplayName,
  extractReceipientId,
  extractUserNameFromAuth0Id
} from "~/lib/helpers";
import { WorkstationSummaryDto } from "~/types/api";

interface AdminSummariesTableProps {
  activeWorkstation: WorkstationSummaryDto;
  enableSideNavSubMenus: boolean;
  openMessageComponent: (
    event: React.MouseEvent | React.KeyboardEvent,
    newRecipients: Recipient[]
  ) => void;
  portErrorsCount?: number;
}

const AdminSummariesContentRow = (props: AdminSummariesTableProps) => {
  const { activeWorkstation, enableSideNavSubMenus, openMessageComponent } =
    props;
  const { t } = useTranslation();
  const navigate = useFcNavigate();
  const dispatch = useAppDispatch();
  const selectedGridId = useAppSelector(selectAdminSummariesGrid);

  const { id, deviceId, userId, mode, roles, ports, status } =
    activeWorkstation;

  const parentPort = ports.find((p) => p.parentPortId !== null);
  const parentPortsDisplay = parentPort?.parentPortId ?? "-";
  const portsDisplay = ports?.map((port) => port.portId).join(", ") || "-";
  const workstationMode = mode !== "Undefined" ? mode : "-";

  const goToErrorDetails = () => {
    dispatch(
      setErrorViewForWorkstation({
        workstationId: id,
        autostoreGridId: selectedGridId,
        errorType: "Port Errors"
      })
    );
    navigate(
      enableSideNavSubMenus
        ? "/service-support/cube-analytics"
        : "/service-support?service-support-tab=cube-analytics"
    );
  };

  const onWorkstationDrillDownViewBtnClick = () => {
    dispatch(setSelectedAdminSummariesWorkstationId(id));
    navigate("/admin-summaries-workstation-view");
  };

  return (
    <Fragment key={id}>
      <ASTableV2Row aria-label={`${status?.toLowerCase()} workstation row`}>
        <ASTableV2Cell>
          <Typography variant="tableBody">{deviceId}</Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Typography variant="tableBody">
            {userId ? extractUserNameFromAuth0Id(userId) : "-"}
          </Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Typography variant="tableBody">{parentPortsDisplay}</Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Typography variant="tableBody">{portsDisplay}</Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Typography variant="tableBody">{workstationMode}</Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Box gap={0.5} display="grid" gridTemplateColumns="repeat(2, auto)">
            {roles.map((role) => (
              <Chip label={role} color="1" key={`aws-${id}-${role}`} />
            ))}
          </Box>
        </ASTableV2Cell>
        <ASTableV2Cell>
          {userId && (
            <IconButton
              aria-label="message user"
              onClick={(event) => {
                openMessageComponent(event, [
                  {
                    workstationUserId: userId,
                    recipientId: extractReceipientId(userId),
                    displayName: extractDisplayName(userId)
                  }
                ]);
              }}
            >
              <Chat20Px width={30} height={30} />
            </IconButton>
          )}
        </ASTableV2Cell>
        <ASTableV2Cell aria-label="port error count">
          <Stack flexDirection="row" gap={2}>
            <Chip
              color="5"
              label={
                <Typography variant="tableBody">
                  {props.portErrorsCount || "-"}
                </Typography>
              }
              sx={{
                borderRadius: "6px",
                minWidth: "40px",
                height: "45px",
                fontSize: "18px"
              }}
            />
            {!!props.portErrorsCount && (
              <Tooltip
                title={t("go to cube analytics port errors")}
                arrow
                placement="bottom"
              >
                <Button
                  variant="subtle"
                  onClick={goToErrorDetails}
                  sx={{
                    minWidth: 40,
                    height: 45,
                    p: 1
                  }}
                  size="small"
                  aria-label="workstations errors view button"
                >
                  <OpenInNewIcon
                    sx={{
                      height: 30,
                      width: 30
                    }}
                  />
                </Button>
              </Tooltip>
            )}
          </Stack>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <IconButton
            onClick={() => {
              onWorkstationDrillDownViewBtnClick();
            }}
            aria-label="workstation details view button"
          >
            <ArrowRight20Px width={30} height={30} />
          </IconButton>
        </ASTableV2Cell>
      </ASTableV2Row>
    </Fragment>
  );
};

export default AdminSummariesContentRow;
